import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import appConfig from 'app-config';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { WatercraftTableActionComponent, WatercraftOperatorsTableActionComponent } from 'ml-capability-quoteandbind-common-react';
import { QuestionSetComponent } from 'ml-components-platform-react';

import metadata from './MLWatercraftListPopup.metadata.json5';
import messages from './MLWatercraftListPopup.messages';
import styles from './MLWatercraftListPopup.module.scss';

const MLWatercraftListPopup = (props) => {
    const {
        id='MLWatercraftListPopup',
        submissionVM,
        updateWizardData,
        addWatercraft,
        editWatercraft,
        removeWatercraft,
        addWatercraftOperator,
        editWatercraftOperator,
        removeWatercraftOperator,
        onCancel,
        onValidate,
        viewModelService,
        showValidationErrors,
        setBannerMessage,
        isPolicyChange
    } = props;

    const isAMP = appConfig.portalCode === 'AMP';
    const translator = useTranslator();
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const watercrafts = _.get(submissionVM, 'lobData.personalUmbrella.coverables.watercrafts.value', []);
    let watercraftQuestionSet = [];
    const [didUpdate, setDidUpdate] = useState(false);
    const { registerComponentValidation, onValidate: setComponentValidation, isComponentValid } = useValidation(id);

    if (watercrafts.length > 0) {
        watercraftQuestionSet = watercrafts[0].watercraftQuestionSet || [];
    }

    const isWaterCraftValid = useCallback(() => {
        const watercraftVM = _.get(submissionVM, 'lobData.personalUmbrella.coverables.watercrafts.children[0]', []);

        if (!_.isEmpty(watercraftVM)) {

            if (
                !watercraftVM.aspects.valid
                || !watercraftVM.aspects.subtreeValid
            ) {
                return false;
            }

            if (watercraftQuestionSet) {
                return Object.values(watercraftQuestionSet[0].answers).every((item) => item !== null)
            }

        }

        return true;
    }, [submissionVM, watercraftQuestionSet]);

    useEffect(() => {
        registerComponentValidation(isWaterCraftValid);
    }, [isWaterCraftValid, registerComponentValidation]);

    
    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);



    const hasErrors = useCallback(() => {
        return watercrafts.filter((watercraft) => watercraft.error === true).length > 0;
    }, [watercrafts]);

    const emptyWatercraftQuestionSet = () => {
        watercraftQuestionSet = [];
    };

    const displayWatercraftQuestionSet = () => {
        if (isModernProduct) {
            return false;
        }
        if (watercrafts) {
            return watercrafts.length > 0;
        }
        return false;
    };

    const onRemoveWatercraft = (item, cancel) => {
        if (watercrafts.length === 0) {
            emptyWatercraftQuestionSet();
        }
        removeWatercraft(item, cancel);
    };

    const onQuestionSetValueChange = (aQuestionSetAnswersValue ) => {
        _.set(submissionVM, 'lobData.personalUmbrella.coverables.watercrafts.value[0].watercraftQuestionSet[0]', aQuestionSetAnswersValue);
        setDidUpdate(!didUpdate);
        updateWizardData(submissionVM);
    }

    const overrides = {
        watercraftNotification: {
            visible: hasErrors(),
            message: (
                <>
                    <h2>{translator(messages.watercraftNeedYourAttention)}</h2>
                    <span>{translator(messages.clickEditLink)}</span>  
                </>
            )
        },
        contentContainer: {
            className: isAMP ? 'amp-ml-watercraft-popup' : 'ml-watercraft-popup'
        },
        watercraftTableComponent: {
            submissionVM,
            updateWizardData,
            onAdd: addWatercraft,
            onEdit: editWatercraft,
            onRemove: onRemoveWatercraft,
            onCancel,
            watercraftQuestionSet,
            emptyWatercraftQuestionSet,
            viewModelService
        },
        watercraftQuestionsetContainer: {
            visible: displayWatercraftQuestionSet()
        },
        watercraftHeaderTitle: {
            className: isAMP ? 'section-header' : 'watercraftHeaderTitle',
            content: isAMP ? translator(messages.reviewThisInfoAndMakeChanges) : translator(messages.watercraftExposures)
        },
        watercraftHeaderMsg: {
            visible: isAMP
        },
        watercraftQuestionset: {
            showHeaderML: false,
            visible: displayWatercraftQuestionSet() && watercraftQuestionSet.length > 0,
            onValueChange: onQuestionSetValueChange,
            showErrors: showValidationErrors
        },
        watercraftOperatorsTableComponent: {
            submissionVM,
            updateWizardData,
            onAdd: addWatercraftOperator,
            onEdit: editWatercraftOperator,
            onRemove: removeWatercraftOperator,
            onCancel,
            viewModelService,
            setBannerMessage,
            isPolicyChange
        }
    };

    const resolvers = {
        resolveComponentMap: {
            questionset: QuestionSetComponent,
            watercraftTableActionComponent: WatercraftTableActionComponent,
            watercraftOperatorsTableActionComponent: WatercraftOperatorsTableActionComponent
        },
        resolveCallbackMap : {},
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={submissionVM}
            onModelChange={updateWizardData}
            onValidationChange={setComponentValidation}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    )
};

MLWatercraftListPopup.propTypes = {
    submissionVM: PropTypes.object,
    updateWizardData: PropTypes.func.isRequired,
    addWatercraft: PropTypes.func,
    editWatercraft: PropTypes.func,
    removeWatercraft: PropTypes.func,
    addWatercraftOperator: PropTypes.func,
    editWatercraftOperator: PropTypes.func,
    removeWatercraftOperator: PropTypes.func,
    onCancel: PropTypes.func,
    onValidate: PropTypes.func
};

export default MLWatercraftListPopup;
